// src/utils/splitBillTotals.js
//
// THE single definition of "what does a split-bill line cost".
//
// SplitBillManager.js computed this inline in three places
// (:135, :147, :302) as `selling_price * quantity`, which silently omitted
// every paid add-on — a split containing a £1.50 Corn undercharged the
// customer by £1.50, live in production. The third copy was worse: it summed
// `selling_price` with no quantity multiplier at all, so a split of two of
// anything charged for one.
//
// Three inline copies is what allowed those to diverge, and it is the same
// failure that produced utils/transactionLines.js on 2026-08-24. This module
// exists so there is one.
//
// It imports NOTHING on purpose, so any caller can use it without dragging in
// axios (whose ESM build Jest cannot parse unmocked).

// A line's cost is (base + every priced modifier) × quantity.
//
// `add_ons` is FLAT and ordered parent-before-child — see
// utils/transactionLines.js's createItemsWithAddons, which builds the rows
// posted to the server from the same array. Nesting therefore needs no
// recursion here: every priced entry in the array belongs to this line, at
// whatever depth. transactionLines.js gives each child row the PARENT
// product's quantity for exactly the same reason, so the two agree.
export const splitLineTotal = (product) => {
    if (!product) return 0;

    const quantity = parseInt(product.quantity, 10) || 1;
    const base = parseFloat(product.selling_price) || 0;

    // Array.isArray, not `|| []`: a corrupt persisted order can carry a
    // non-array add_ons, and .reduce on it would throw mid-split.
    const addOns = Array.isArray(product.add_ons) ? product.add_ons : [];
    const modifiers = addOns.reduce(
        (sum, addon) => sum + (parseFloat(addon?.price) || 0),
        0
    );

    return round2((base + modifiers) * quantity);
};

export const splitSubtotal = (products) => {
    const list = Array.isArray(products) ? products : [];
    return round2(list.reduce((sum, product) => sum + splitLineTotal(product), 0));
};

// Money must not carry binary-float drift into a payment amount.
function round2(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
